import React, {MutableRefObject, useContext, useEffect, useState} from 'react';

import {useTypedFastStorage} from '../../../hooks/useFastStorage';

import {CustomerVendor} from "../../../ifmsTypes";
import RadioButton from "../../../ui/RadioButton";
import InlineInputControlRow from "../../../ui/layout/InlineInputControlRow";
import Date from "../../../ui/Date";
import LeftRightSplit from "../../../ui/LeftRightSplit";
import CheckBox from "../../../ui/CheckBox";
import BranchDropDown from "../../../uiControls/BranchDropDown";
import SecondaryButton from "../../../ui/SecondaryButton";
import MultiNameDropDown from "../../../uiControls/MultiNameDropDown";
import CompanyListNotAgentLookupField from "../../../uiControls/lookup/CompanyListNotAgentLookupField";
import Select from "../../../ui/Select";
import Fieldset from "../../common/Fieldset";
import { formatDate, getDate, now } from '../../../dateUtils';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import useReportViewer from '../../../hooks/useReportViewer';
import { getSelectedCheckValues } from '../../../fms.data/modules/FmsModFunc';
import { LocalStatementArgs } from '../../../fms.report/args';
import {DocumentType} from "../../../framework.core/DocumentType";
import {LookupCompany} from "../../../fms.data/lookup";
import ApplicationContext from '../../../ApplicationContext';
import Label from '../../../ui/Label';
import CurrencyDropDown from '../../../uiControls/CurrencyDropDown';
import { focusTabableByIndex } from '../../../util/inputUtils';

interface CustomerVendorStorageKeys {
  modelStorage: string,
};

interface CustomerVendorViewParams {
  id: string | undefined,
  customerName: string | '',
  periodType: string | '',
  onPopupClose?: () => void
};

interface CustomerVendorLoadedDetailsViewParams {
  customerVendor: CustomerVendor,
  customerVendorRef: MutableRefObject<CustomerVendor | null>,
  setCustomerVendor: (x: Partial<CustomerVendor>) => void,
  id: string | undefined,
  onPopupClose?: () => void
};

function getCustomerVendorStorageKeys(): CustomerVendorStorageKeys {
  return {modelStorage: 'report-customer-vendor-statement'}
}

function CustomerVendorImpl(params: CustomerVendorViewParams) {
  const {id, customerName, periodType} = params;
  const {modelStorage} = getCustomerVendorStorageKeys();
  const [inStorage, setInStorage, removeCustomerVendorFromStorage, customerVendorRef] = useTypedFastStorage<CustomerVendor>(modelStorage, null);
  const [initialized, setInitialized] = useState(false);

  // const appContext = useContext(ApplicationContext);

  useEffect(() => {
      initialize();
      return () => {
        removeCustomerVendorFromStorage()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function loadExisting() {
    let periodCombo;
    if (periodType === 'PASTDUE') {
      periodCombo = 'Due';
    } else if (periodType === 'Recog') {
      periodCombo = 'Recog';
    } else {
      periodCombo = 'Invoice';
    }

    return {
      customerId: id? id : 0,
      customerName,
      periodCombo,
      toDate: now(),
      // OI: 'OI',
      // OO: 'OO',
      // AI: 'AI',
      // AO: 'AO',
      // OT: 'GE',
      WH: 'WS,WR',
      GEN: 'GEN',
      BT: 'BO,BL',
      sort: 'F_InvoiceDate',
      filter: 'OPEN',
      invoiceData: true,
      billData: true,
      billReceivedOnly: false,
      showPrevBalance: true,
      doNotShowOverpaidInvoices: false,
      attachInvoices: false,
      branch: 'ALL',
      currency: 'ALL',
      //recognizedOnly: false,
    }
  }

  async function initialize() {
    if (!inStorage) {
      const data = await loadExisting();
      setInStorage({...data})
    }
    setInitialized(true);
  }

  if (!initialized || !inStorage) {
    return null;
  }

  return (
    <CustomerVendorInitializedImpl
      customerVendor={inStorage}
      customerVendorRef={customerVendorRef}
      setCustomerVendor={setInStorage}
      id={id}
      onPopupClose={params.onPopupClose}
    />
  );
}

function CustomerVendorInitializedImpl({
                                         customerVendor,
                                         customerVendorRef,
                                         setCustomerVendor,
                                         id,
                                         onPopupClose
                                       }: CustomerVendorLoadedDetailsViewParams) {
  const {viewReport} = useReportViewer();
  const [showGlobalOkDialog] = useGlobalOkDialog();
  const appContext = useContext(ApplicationContext);
  
  useEffect(() => {
    focusTabableByIndex(0);
  }, []);
  
  function onChange(change: Partial<CustomerVendor>) {
    setCustomerVendor(change);
  }

  function selectAll() {
    onChange({
      // OI: 'OI',
      // OO: 'OO',
      // AI: 'AI',
      // AO: 'AO',
      // OT: 'GE',
      WH: 'WS,WR',
      GEN: 'GEN',
      BT: 'BO,BL'
    })
  }

  function clearAll() {
    onChange({
      // OI: '',
      // OO: '',
      // AI: '',
      // AO: '',
      // OT: '',
      WH: '',
      GEN: '',
      BT: ''
    })
  }

  let disableBillReceivedOnly = customerVendor.billData ? false: true;
  useEffect(() => {
    if(!customerVendor.billData){
      onChange({billReceivedOnly: false});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[customerVendor.billData]);

  async function print(){
    //#region CheckPrintOptions

    let errorMsg = '';
    let invobillData = '';
    let departs = '';

    const fromDate = getDate(customerVendor.fromDate)??undefined;
    const toDate = getDate(customerVendor.toDate)??undefined;

    if(!toDate){
      errorMsg = "Please enter valid ending date.";
    }

    invobillData = getSelectedCheckValues(
      [
        (customerVendor.invoiceData ? 'Local Invoice' : ''), 
        (customerVendor.billData ? 'Accounts Payable' : ''), 
      ], ",");

    if(!invobillData){
      errorMsg = errorMsg + (errorMsg.length ? "\r\n" : "") + "Report Type check at least one";
    }

    departs = getSelectedCheckValues(
      [
        // customerVendor.OI, 
        // customerVendor.OO, 
        // customerVendor.AI, 
        // customerVendor.AO, 
        // customerVendor.OT,
        customerVendor.WH,
        customerVendor.GEN,
        customerVendor.BT,
      ], ",");

    if(!departs){
      errorMsg = errorMsg + (errorMsg.length ? "\r\n" : "") + "Type check at least one";
    }
    if(errorMsg){
      await showGlobalOkDialog(<p>{errorMsg}</p>, false, "Error");
      return;
    }

    //#endregion  
    
    departs = departs.replace('GEN','');
    const title = customerVendor.customerName ? 
      `Statement for ${customerVendor.customerName} on ${formatDate(now())}`:
      `Statement on ${formatDate(now())}`;
    const args = new LocalStatementArgs({
      docType: DocumentType.LocalStatement,
      formCaption: title,
      subject: title,
      useMultiCur: appContext.suntekSettings?.accounting?.showAmountByCurrencyInInvoice??false,
      pastDue: '',
      excl0Term: false,

      toCompanyid: customerVendor.customerId.toString(),
      billto: customerVendor.customerId,
      startDate: fromDate,
      endDate: toDate,
      reportType: invobillData,
      paidType: customerVendor.filter === 'All' ? '' : customerVendor.filter,
      dataCompanyID: customerVendor.nameOnReport?? 0,
      branch: customerVendor.branch === 'ALL' ? '' : customerVendor.branch,
      showDetail: customerVendor.attachInvoices,
      dateType: customerVendor.periodCombo,
      sortType: customerVendor.sort,
      currency: customerVendor.currency === 'ALL' ? '' : customerVendor.currency,
      advance: customerVendor.companyGroup,
      asOf: fromDate ? 'Period' : 'As of',
      invoiceOnly: customerVendor.billReceivedOnly,
      pBalShow: customerVendor.showPrevBalance,
      notShowOver:customerVendor.doNotShowOverpaidInvoices,
      completeInvo:'0',
      departs: departs + ",",
      //recognizedOnly: customerVendor.recognizedOnly, 사용하지 않는 필드
      //departsIL:'', 사용하지 않는 필드
    });

    viewReport(args, "", true);
  }

  const isSelectAll =
    // customerVendor.OI === 'OI' &&
    // customerVendor.OO === 'OO' &&
    // customerVendor.AI === 'AI' &&
    // customerVendor.AO === 'AO' &&
    // customerVendor.OT === 'GE' &&
    customerVendor.WH === 'WS,WR' &&
    customerVendor.GEN === 'GEN' &&
    customerVendor.BT === 'BO,BL';
  
  return (
    <div className='report-popup'>
      <Fieldset label='Company'>
        {appContext.systemSettings?.useMultipleNames ? 
          <div style={{display: 'flex'}}>
            <div style={{marginLeft: '80px', width: '460px'}}>
              <InlineInputControlRow label='Statement Issuer' mode='smaller-2x'>
                <MultiNameDropDown
                  onChange={e => onChange({nameOnReport: e?.intValue})}
                  value={customerVendor.nameOnReport}
                  loadFromConfig
                />
              </InlineInputControlRow>
            </div>
          </div> : null}

        <div style={{display: 'flex'}}>
          <div style={{marginLeft: '80px', width: '460px'}}>
            <InlineInputControlRow label='Customer Name' mode='smaller-2x'>
              <CompanyListNotAgentLookupField
                onSelect={e => {
                  const company = e as LookupCompany;
                  onChange({customerName: company.shortName, customerId: company.id})
                }}
                onTextChange={customerName => onChange({customerName: customerName||''})}
                onBlur={() => {
                  if (customerVendor.customerId === 0) {
                    onChange({ customerName: '' });
                  }
                }}
                initialValue={customerVendor.customerName}
                disabled={id ? true : false}
              />
            </InlineInputControlRow>
          </div>
          <div style={{paddingLeft: '10px'}}>
            (Blank for All)
          </div>
        </div>
      </Fieldset>
      <Fieldset label='Period & Currency'>
        <div style={{display: 'flex'}}>
          <div style={{marginLeft: '80px', width: '110px'}}>
            <Select
                value={customerVendor.periodCombo}
                onChange={e => onChange({periodCombo: e.target.value})}
                items={[{
                  value: 'Invoice', label: 'Invoice/Bill Date',
                }, {
                  value: 'Due', label: 'Due Date'
                }, {
                  value: 'Recog', label: 'Recog Date'
                }]}
              />
          </div>
          <div style={{marginLeft: '40px', width: '80px'}}>
            <Date value={customerVendor.fromDate} onChange={fromDate => onChange({fromDate})} />
          </div>

          <div style={{width: '60px', paddingLeft: '32px'}}>
            ~
          </div>

          <div style={{width: '80px'}}>
            <Date value={customerVendor.toDate} onChange={toDate => onChange({toDate})} />
          </div>

          <div style={{marginLeft: '80px', width: '120px'}}>
            <InlineInputControlRow label="Currency" mode="w60">
              <CurrencyDropDown
                value={customerVendor.currency}
                onChange={currency => onChange({currency})}
                allowEmpty={false}
                additionalElements={[{label: 'ALL', value: 'ALL'}]}
              />
            </InlineInputControlRow>
          </div>

        </div>
      </Fieldset>
      <Fieldset label='Operation'>
        <div style={{display: 'flex'}}>
          <div style={{marginLeft: '150px', width: '100px'}}>
            <CheckBox
              label="All"
              value={isSelectAll}
              onChange={checked => {
                if (checked) {
                  selectAll();
                } else {
                  clearAll()
                }
              }}
            />
          </div>

          <div style={{marginLeft: '40px', width: '100px'}}>
            {/* <CheckBox
              label="Ocean Import"
              value={customerVendor.OI}
              onChange={checked => onChange({OI: checked? 'OI': ''})}
            /> */}
            <CheckBox
              label='General'
              value={customerVendor.GEN}
              onChange={checked => onChange({GEN: checked? 'GEN' : ''})}
            />
          </div>

          <div style={{marginLeft: '40px'}}>
            {/* <CheckBox
              label='Ocean Export'
              value={customerVendor.OO}
              onChange={checked => onChange({OO: checked? 'OO' : ''})}
            /> */}
            <CheckBox
              label='Warehousing'
              value={customerVendor.WH}
              onChange={checked => onChange({WH: checked? 'WS,WR' : ''})}
            />
          </div>
        </div>

        <div style={{display: 'flex', marginTop: '10px'}}>
          <div style={{marginLeft: '150px', width: '100px'}}>
            {/* <CheckBox
              value={customerVendor.AI}
              onChange={checked => onChange({AI: checked? 'AI' : ''})}
              label="Air Import"
            /> */}
            <CheckBox
              label='Brokerage Trucking'
              value={customerVendor.BT}
              onChange={checked => onChange({BT: checked? 'BO,BL' : ''})}
            />
          </div>

          <div style={{marginLeft: '40px', width: '100px'}}>
            {/* <CheckBox
              label='Air Export'
              value={customerVendor.AO}
              onChange={checked => onChange({AO: checked? 'AO' : ''})}
            /> */}
          </div>

          <div style={{marginLeft: '40px'}}>
            {/* <CheckBox
              label='General'
              value={customerVendor.GEN}
              onChange={checked => onChange({GEN: checked? 'GEN' : ''})}
            /> */}
          </div>
        </div>

        {/* <div style={{display: 'flex', marginTop: '10px'}}>
          <div style={{marginLeft: '150px', width: '100px'}}>
            <CheckBox
              label='Warehouse'
              value={customerVendor.WH}
              onChange={checked => onChange({WH: checked? 'WS,WR' : ''})}
            />
          </div>

          <div style={{marginLeft: '40px', width: '100px'}}>
            <CheckBox
              value={customerVendor.OT}
              onChange={checked => onChange({OT: checked? 'GE' : ''})}
              label="Other Shipment"
            />
          </div>

          <div style={{marginLeft: '40px'}}>

          </div>
        </div> */}
      </Fieldset>
      <Fieldset label='Filter'>
        <div style={{display: 'flex'}}>
          <div style={{marginLeft: '210px', width: '180px'}}>
            <RadioButton group="customer-vendor-filter" reverse label="All"
              checked={customerVendor.filter === 'All'}
              onChecked={() => onChange({filter: 'All'})}
              disabled={false}/>
          </div>

          <div>
            <RadioButton group="customer-vendor-filter" reverse label="Open"
              checked={customerVendor.filter === 'OPEN'}
              onChecked={() => onChange({filter: 'OPEN'})}
              disabled={false}/>
          </div>

        </div>
      </Fieldset>

      <Fieldset label='Sort Order'>
        <div style={{display: 'flex'}}>
          <div style={{marginLeft: '150px', width: '140px'}}>
            <RadioButton group="customer-vendor-sort" reverse label="Invoice/Bill Date"
              checked={customerVendor.sort === 'F_InvoiceDate'}
              onChecked={() => onChange({sort: 'F_InvoiceDate'})}
              disabled={false}/>
          </div>

          <div style={{width: '140px'}}>
            <RadioButton group="customer-vendor-sort" reverse label="Invoice/Bill #"
              checked={customerVendor.sort === 'F_InvoiceNo'}
              onChecked={() => onChange({sort: 'F_InvoiceNo'})}
              disabled={false}/>
          </div>

          <div>
            <RadioButton group="customer-vendor-sort" reverse label="Due Date"
              checked={customerVendor.sort === 'F_DueDate'}
              onChecked={() => onChange({sort: 'F_DueDate'})}
              disabled={false}/>
          </div>
        </div>
      </Fieldset>

      <Fieldset label='Option'>
        <div style={{display: 'flex'}}>
          <div style={{marginLeft: '120px', width: '240px'}}>
            <CheckBox
              label='Invoice'
              focusId="customer-invoiceData"
              value={customerVendor.invoiceData}
              onChange={invoiceData => onChange({invoiceData})}
            />
          </div>
          <div style={{marginRight:"100px"}}>
            <CheckBox
              label='Bill'
              value={customerVendor.billData}
              onChange={billData => onChange({billData})}
            />
          </div>
        </div>

        <div style={{display: 'flex', marginTop: '10px'}}>
          <div style={{marginLeft: '120px', width: '240px'}}>
            <CheckBox
              label='Attach Invoice(s)'
              value={customerVendor.attachInvoices}
              onChange={attachInvoices => onChange({attachInvoices})}
            />
          </div>
          <div>
            <CheckBox
              label='Bill Received Only'
              value={customerVendor.billReceivedOnly}
              onChange={billReceivedOnly => onChange({billReceivedOnly})}
              disabled={disableBillReceivedOnly}
            />
          </div>
        </div>

        <div style={{display: 'flex', marginTop: '10px'}}>
          <div style={{marginLeft: '120px', width: '240px'}}>
            <CheckBox
              label='Do not show Overpaid Invoice(s)'
              value={customerVendor.doNotShowOverpaidInvoices}
              onChange={doNotShowOverpaidInvoices => onChange({doNotShowOverpaidInvoices})}
            />
          </div>
          <div>
            <CheckBox
              label='Show Previous Balance'
              value={customerVendor.showPrevBalance}
              onChange={showPrevBalance => onChange({showPrevBalance})}
            />
          </div>
        </div>
      </Fieldset>

      {appContext.systemSettings?.enableBranch &&
        <Fieldset label='Select Branch'>
          <div style={{display: 'flex'}}>
            <div style={{marginLeft: '260px'}}>
              <LeftRightSplit
                leftSize='60px'
                rightSize='calc(100% - 60px)'
                left={
                  <div className='text-right mr-2'><Label label='Branch' /></div>
                }
                right={
                  <div style={{width: '80px'}}>
                    <BranchDropDown
                      disabled={false}
                      value={customerVendor?.branch}
                      onChange={branch => onChange({branch})}
                      addAll
                    />
                  </div>

                }
              />
            </div>
          </div>
        </Fieldset>
      }
      <div className='report-footer'>
        <SecondaryButton
          focusable={true}
          size="regular"
          style={{width: '90px'}}
          onClick={() => {
            print()
          }}>
          PREVIEW
        </SecondaryButton>
        <SecondaryButton
          focusable={true}
          size="regular"
          style={{width: '90px'}}
          onClick={onPopupClose}>
          CANCEL
        </SecondaryButton>
      </div>
    </div>
  );
}

const CustomerVendorDetails = ({
                                onPopupClose,
                                tbId,
                                customerName,
                                periodType,
                                selectedElement
                              }: { 
                                onPopupClose?: () => void,
                                tbId: string | '',
                                customerName: string |'',
                                periodType: string |'',
                                selectedElement?: any
                              }) => {
  // const { id } = useParams<CustomerVendorViewParams>();
  if (!tbId && !customerName) {
    if (!selectedElement) {
      tbId = '';
      customerName = '';
    }
    else {
      tbId = selectedElement.id ?? '';
      customerName = selectedElement.customerName ?? '';
    }
  }

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  return (
    <CustomerVendorImpl id={tbId} customerName={customerName} periodType={periodType} onPopupClose={onPopupClose} key={`customerVendor-details`}/>
  );
};
export default CustomerVendorDetails;
